import { useEffect, useMemo, useState } from 'react';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import {
  InsightViewCellColorCondition,
  InsightViewCellColorRuleItem,
} from 'types/api/SystemTenant/AROView/InsightViewDefinitionTypes';
import { INSIGHT_VIEW_CELL_COLOR_OPTIONS, MAP_COLOR_CODE_WITH_COLOR_HEXA } from 'constant/ViewConstant';
import {
  CELL_COLOR_CONDITION_OPTIONS,
  CONDITION_SYMBOL,
  isBetweenCondition,
} from 'pages/SystemTenant/AROView/InsightViewDefinition/components/ModalCellColorRule';

const ModalContentStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 560,
  maxWidth: '95vw',
  bgcolor: 'background.paper',
  maxHeight: '90vh',
  overflow: 'auto',
  borderRadius: 1,
  py: 2,
};

const sectionBoxStyle = {
  bgcolor: '#FBFBFB',
  py: 1.5,
  px: 1.5,
  borderRadius: 1,
  border: '1px solid #E3EBF6',
};

const stepBadgeStyle = {
  width: 22,
  height: 22,
  borderRadius: '50%',
  bgcolor: '#3B4797',
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

export interface ModalCellColorRuleEditorProps {
  visible?: boolean;
  onClose?: () => void;
  onSave?: (rule: InsightViewCellColorRuleItem) => void;
  initialData?: InsightViewCellColorRuleItem | null;
  attributeReferenceOptions: AutoCompleteItem[];
}

export function ModalCellColorRuleEditor(props: Readonly<ModalCellColorRuleEditorProps>) {
  const { visible = false, onClose, onSave, initialData, attributeReferenceOptions } = props;

  const [condition, setCondition] = useState<AutoCompleteItem | null>(null);
  const [value, setValue] = useState<string>('');
  const [minValue, setMinValue] = useState<string>('');
  const [maxValue, setMaxValue] = useState<string>('');
  const [includeMin, setIncludeMin] = useState<boolean>(false);
  const [includeMax, setIncludeMax] = useState<boolean>(false);
  const [color, setColor] = useState<AutoCompleteItem | null>(null);
  const [attributeReference, setAttributeReference] = useState<AutoCompleteItem | null>(null);
  const [previewValue, setPreviewValue] = useState<string>('5');

  useEffect(() => {
    if (!visible) return;
    if (initialData) {
      setCondition(initialData.condition);
      setValue(initialData.value ?? '');
      setMinValue(initialData.minValue ?? '');
      setMaxValue(initialData.maxValue ?? '');
      setIncludeMin(!!initialData.includeMin);
      setIncludeMax(!!initialData.includeMax);
      setColor(initialData.color);
      setAttributeReference(initialData.attributeReference);
      setPreviewValue(initialData.value ?? initialData.minValue ?? '5');
    } else {
      setCondition(null);
      setValue('');
      setMinValue('');
      setMaxValue('');
      setIncludeMin(false);
      setIncludeMax(false);
      setColor(null);
      setAttributeReference(null);
      setPreviewValue('5');
    }
  }, [visible, initialData]);

  const between = isBetweenCondition(condition);
  const colorHexa = color ? MAP_COLOR_CODE_WITH_COLOR_HEXA.get(color.value.toString()) : undefined;

  const betweenRangeInvalid = useMemo(() => {
    if (!between || minValue === '' || maxValue === '') return false;
    const min = parseFloat(minValue);
    const max = parseFloat(maxValue);
    return !Number.isNaN(min) && !Number.isNaN(max) && min > max;
  }, [between, minValue, maxValue]);

  const isValid = useMemo(() => {
    if (!condition || !color) return false;
    if (between) {
      return minValue !== '' && maxValue !== '' && !betweenRangeInvalid;
    }
    return value !== '' || attributeReference != null;
  }, [condition, color, between, minValue, maxValue, betweenRangeInvalid, value, attributeReference]);

  const previewMatch: boolean | null = (() => {
    if (!condition) return null;
    const sample = parseFloat(previewValue);
    if (Number.isNaN(sample)) return null;
    if (between) {
      const min = parseFloat(minValue);
      const max = parseFloat(maxValue);
      if (Number.isNaN(min) || Number.isNaN(max)) return null;
      const minOk = includeMin ? sample >= min : sample > min;
      const maxOk = includeMax ? sample <= max : sample < max;
      return minOk && maxOk;
    }
    if (attributeReference) return null;
    if (!value) return null;
    const threshold = parseFloat(value);
    if (Number.isNaN(threshold)) return null;
    switch (condition.value) {
      case InsightViewCellColorCondition.EQUAL:
        return sample === threshold;
      case InsightViewCellColorCondition.GREATER_THAN:
        return sample > threshold;
      case InsightViewCellColorCondition.LESS_THAN:
        return sample < threshold;
      case InsightViewCellColorCondition.NUMERIC_GREATER_THAN_OR_EQUAL:
        return sample >= threshold;
      case InsightViewCellColorCondition.NUMERIC_LESS_THAN_OR_EQUAL:
        return sample <= threshold;
      default:
        return null;
    }
  })();

  const handleSave = () => {
    if (!isValid || !condition || !color) return;
    const next: InsightViewCellColorRuleItem = {
      id: initialData?.id ?? null,
      condition,
      color,
      attributeReference: between ? null : attributeReference,
      value: between ? null : value || null,
      minValue: between ? minValue : null,
      maxValue: between ? maxValue : null,
      includeMin: between ? includeMin : false,
      includeMax: between ? includeMax : false,
      priorityOrder: initialData?.priorityOrder ?? 0,
    };
    onSave?.(next);
  };

  return (
    <Modal open={visible} onClose={onClose}>
      <Stack direction='column' sx={ModalContentStyle} px={3} spacing={2}>
        {/* ── Header ── */}
        <Stack justifyContent='space-between' direction='row' alignItems='center'>
          <Stack direction='row' alignItems='center' spacing={1.25}>
            <RuleRoundedIcon sx={{ color: '#42BB93' }} />
            <Stack spacing={0}>
              <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797', lineHeight: 1.2 }}>
                {initialData ? 'Edit Rule' : 'Add Rule'}
              </Typography>
              <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                When the condition matches, the cell gets the colour
              </Typography>
            </Stack>
          </Stack>
          <IconButton onClick={onClose} component='label'>
            <HighlightOffRoundedIcon sx={{ color: '#98A2AE', cursor: 'pointer' }} />
          </IconButton>
        </Stack>
        <Divider />

        {/* ── Step 1: Condition ── */}
        <Stack spacing={1.5} sx={sectionBoxStyle}>
          <Stack direction='row' alignItems='center' spacing={1}>
            <Box sx={stepBadgeStyle}>1</Box>
            <TuneOutlinedIcon fontSize='small' sx={{ color: '#3B4797' }} />
            <Stack spacing={0}>
              <Typography variant='body2' fontWeight={700} sx={{ color: '#3B4797', lineHeight: 1.2 }}>
                Condition
              </Typography>
              <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                When the cell value is...
              </Typography>
            </Stack>
          </Stack>

          <ToggleButtonGroup
            value={condition?.value ?? null}
            exclusive
            onChange={(_, newValue) => {
              if (newValue == null) return;
              const opt = CELL_COLOR_CONDITION_OPTIONS.find((o) => o.value === newValue);
              if (opt) setCondition(opt);
            }}
            fullWidth
            size='small'
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                py: 1,
                bgcolor: '#fff',
                borderColor: '#D0D5DD',
                color: '#475467',
              },
              '& .MuiToggleButton-root.Mui-selected': {
                bgcolor: '#3B4797',
                color: '#fff',
                '&:hover': { bgcolor: '#2F3A7A' },
              },
            }}
          >
            {CELL_COLOR_CONDITION_OPTIONS.map((opt) => {
              const v = opt.value as string;
              const isWord = v === InsightViewCellColorCondition.BETWEEN;
              return (
                <ToggleButton key={v} value={v} title={opt.label} aria-label={opt.label}>
                  <span style={{ fontSize: isWord ? 13 : 20, fontWeight: 600, lineHeight: 1 }}>
                    {CONDITION_SYMBOL[v]}
                  </span>
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>

          {between ? (
            <Stack spacing={1}>
              <Stack direction='row' spacing={2}>
                <TextField
                  size='small'
                  label='Min'
                  value={minValue}
                  onChange={(e) => setMinValue(e.target.value)}
                  placeholder='0'
                  error={betweenRangeInvalid}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size='small'
                  label='Max'
                  value={maxValue}
                  onChange={(e) => setMaxValue(e.target.value)}
                  placeholder='0'
                  error={betweenRangeInvalid}
                  sx={{ flex: 1 }}
                />
              </Stack>
              {betweenRangeInvalid && (
                <Typography variant='caption' sx={{ color: '#D92D20' }}>
                  Min must be less than or equal to Max.
                </Typography>
              )}
              <Stack direction='row' spacing={4}>
                <Stack direction='row' alignItems='center'>
                  <Checkbox
                    checked={includeMin}
                    onChange={(e) => setIncludeMin(e.target.checked)}
                    sx={{ color: '#828C99', px: 0, paddingRight: 0.5 }}
                  />
                  <Typography variant='caption' sx={{ color: '#475467', fontWeight: 600 }}>
                    Include Min
                  </Typography>
                </Stack>
                <Stack direction='row' alignItems='center'>
                  <Checkbox
                    checked={includeMax}
                    onChange={(e) => setIncludeMax(e.target.checked)}
                    sx={{ color: '#828C99', px: 0, paddingRight: 0.5 }}
                  />
                  <Typography variant='caption' sx={{ color: '#475467', fontWeight: 600 }}>
                    Include Max
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={1}>
              <Stack direction='row' spacing={2} alignItems='center'>
                <TextField
                  size='small'
                  label='Value'
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder='0'
                  disabled={attributeReference != null}
                  sx={{ flex: 1 }}
                />
                <Typography variant='caption' sx={{ color: '#98A2AE', flexShrink: 0 }}>
                  or
                </Typography>
                <Autocomplete
                  size='small'
                  sx={{ flex: 1.4 }}
                  value={attributeReference}
                  onChange={(_, v) => setAttributeReference(v)}
                  options={attributeReferenceOptions}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  renderInput={(p) => <TextField {...p} label='Compare to Measure' />}
                />
              </Stack>
              <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                Use a fixed value, or compare the cell against another measure instead.
              </Typography>
            </Stack>
          )}
        </Stack>

        {/* ── Step 2: Colour ── */}
        <Stack spacing={1.5} sx={sectionBoxStyle}>
          <Stack direction='row' alignItems='center' spacing={1}>
            <Box sx={stepBadgeStyle}>2</Box>
            <PaletteOutlinedIcon fontSize='small' sx={{ color: '#3B4797' }} />
            <Stack spacing={0}>
              <Typography variant='body2' fontWeight={700} sx={{ color: '#3B4797', lineHeight: 1.2 }}>
                Colour
              </Typography>
              <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                ...paint the cell with
              </Typography>
            </Stack>
          </Stack>

          <Stack direction='row' flexWrap='wrap' sx={{ gap: 1 }}>
            {INSIGHT_VIEW_CELL_COLOR_OPTIONS.map((option) => {
              const hexa = MAP_COLOR_CODE_WITH_COLOR_HEXA.get(option.value.toString());
              const isSelected = color?.value === option.value;
              return (
                <Tooltip key={option.value} title={option.label}>
                  <Box
                    onClick={() => setColor(option)}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      bgcolor: hexa,
                      border: '1px solid rgba(0, 0, 0, 0.15)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      outline: isSelected ? '2px solid #3B4797' : 'none',
                      outlineOffset: 2,
                      transition: 'transform 120ms ease',
                      '&:hover': { transform: 'scale(1.1)' },
                    }}
                  >
                    {isSelected && (
                      <CheckRoundedIcon sx={{ fontSize: 18, color: 'rgba(0, 0, 0, 0.55)' }} />
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Stack>
          <Typography variant='caption' sx={{ color: '#98A2AE' }}>
            {color ? (
              <>
                Selected:{' '}
                <Box component='span' sx={{ fontWeight: 700, color: '#3B4797' }}>
                  {color.label}
                </Box>
              </>
            ) : (
              'Pick the colour applied when the condition matches.'
            )}
          </Typography>
        </Stack>

        {/* ── Live preview ── */}
        <Box sx={{ px: 1.5, py: 1.25, border: '1px dashed #C8D7EC', borderRadius: 1 }}>
          <Typography
            variant='caption'
            sx={{ color: '#98A2AE', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}
          >
            Preview
          </Typography>
          <Stack direction='row' alignItems='center' spacing={1.5} flexWrap='wrap' sx={{ mt: 0.75 }}>
            <Typography variant='body2' sx={{ color: '#475467' }}>
              If a cell's value is
            </Typography>
            <TextField
              size='small'
              value={previewValue}
              onChange={(e) => setPreviewValue(e.target.value)}
              sx={{ width: 90 }}
            />
            <Typography variant='body2' sx={{ color: '#98A2AE' }}>
              it renders as
            </Typography>
            <Box
              sx={{
                minWidth: 84,
                px: 1.5,
                py: 0.75,
                borderRadius: 0.5,
                border: '1px solid #E3EBF6',
                bgcolor: previewMatch && colorHexa ? colorHexa : '#fff',
                textAlign: 'center',
                fontSize: 13,
                fontWeight: 600,
                color: '#333',
                transition: 'background-color 200ms ease',
              }}
            >
              {previewValue || '...'}
            </Box>
            {attributeReference && !between ? (
              <Typography variant='caption' sx={{ color: '#98A2AE', fontStyle: 'italic' }}>
                preview unavailable when comparing against another measure
              </Typography>
            ) : previewMatch === null ? (
              <Typography variant='caption' sx={{ color: '#98A2AE', fontStyle: 'italic' }}>
                fill in the rule to preview
              </Typography>
            ) : (
              <Typography
                variant='caption'
                sx={{ color: previewMatch ? '#1B7F45' : '#98A2AE', fontWeight: 600 }}
              >
                {previewMatch ? `Match: ${color?.label ?? 'pick a colour'}` : 'No match: no colour applied'}
              </Typography>
            )}
          </Stack>
        </Box>

        <Divider />
        {/* ── Footer ── */}
        <Stack direction='row' justifyContent='flex-end' spacing={1.5}>
          <Button variant='main-table-panel-border' onClick={onClose} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant='main-table-panel'
            disabled={!isValid}
            onClick={handleSave}
            sx={{ textTransform: 'none' }}
          >
            Save Rule
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}
